import { getStoredMeta, loadStoredSession } from "@itaas/core/auth/auth.storage";
import { getPort } from "@itaas/ports";
import { ISC_API_BASE_URL, ISC_TENANT_SLUG } from "./config";

const ISC_API_PORT = getPort("inventory", "api");

export class IscApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "IscApiError";
  }
}

interface IscApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

function tenantSlug(): string {
  return loadStoredSession()?.tenant?.slug ?? ISC_TENANT_SLUG;
}

async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Tenant-Slug": tenantSlug(),
  };
  const { accessToken } = getStoredMeta();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return headers;
}

async function parseBody<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text.trim()) {
    throw new IscApiError(
      `API stock inaccessible — démarrez inventory API (port ${ISC_API_PORT}).`,
      response.status,
    );
  }
  const json = JSON.parse(text) as IscApiResponse<T> | { message?: string };
  if (!response.ok) {
    const message =
      "message" in json && json.message ? json.message : `Erreur API (${response.status})`;
    throw new IscApiError(message, response.status);
  }
  if (json && typeof json === "object" && "success" in json && "data" in json) {
    return (json as IscApiResponse<T>).data;
  }
  return json as T;
}

export async function iscRequest<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers = await buildHeaders();
  const response = await fetch(`${ISC_API_BASE_URL}${path}`, {
    method,
    headers,
    body:
      method === "GET" || method === "DELETE"
        ? undefined
        : JSON.stringify(options.body ?? {}),
  }).catch(() => {
    throw new IscApiError(
      `API stock inaccessible — démarrez inventory API (port ${ISC_API_PORT}).`,
      0,
    );
  });
  return parseBody<T>(response);
}
